import ClickAwayListener from '@components/App/ClickAwayListener/ClickAwayListener'
import Recaptcha from '@components/App/RecaptchaWidget'
import { Loading } from '@lib/components'
import { IconRight } from '@lib/icons'
import { metaActions } from '@store/reducers/manager/meta'
import {
  NotificationType,
  notificationActionCreators,
} from '@storeOld/reducers/notification'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import API from '@/api'
import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { assert } from '@/helpers'
import { migrationProjectSelector } from '@/store/migrationSelectors/projectSelectors'
import css from './Invite.module.scss'

// @ts-expect-error
// biome-ignore lint/suspicious/noShadowRestrictedNames: Error is a domain component name
const Error: React.FC = ({ children }) => (
  <div className="invite-error">{children}</div>
)

const Invite: React.FC = () => {
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(false)
  const [showInput, setShowInput] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const [inputError, setInputError] = useState(false)
  const [value, setValue] = useState('')
  const projectId = useSelector(migrationProjectSelector.getProjectId)
  const isReport = useSelector(migrationProjectSelector.getProjectIsReport)

  useEffect(() => {
    if (showInput && inputRef.current) {
      inputRef.current.focus()
    }
  }, [showInput])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputError(false)
    setValue(e.target.value)
  }

  const onInvite = async (e: React.ChangeEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (isLoading) {
      return
    }

    if (inputRef.current) {
      const { value } = inputRef.current

      if (assert.emailAddress(value)) {
        inputError && setInputError(false)
        setIsLoading(true)

        try {
          const members = await API.Invitation.create({
            projectId,
            recipients: [value],
          })

          // Invite sent — count it here, after a valid email and a successful
          // request, not on submit (which also fired for invalid emails and
          // failed requests).
          sendGtmEvent(GtmTriggerName.PROJECT_INVITE)

          if (isReport) {
            sendGtmEvent(GtmTriggerName.REPORT_SHARE_INVITE)
          }

          // Invites are handled by the legacy backend, which knows nothing
          // about reports and hands out its usual permission. A report only
          // has viewers, so anything else is put back to view right away.
          if (isReport) {
            await Promise.all(
              (members as { id: string; permission?: string }[])
                .filter((member) => member.permission !== 'view')
                .map((member) =>
                  API.Projects.memberPermission({
                    projectId,
                    memberId: member.id,
                    permission: 'view',
                  })
                )
            )
          }

          dispatch(
            metaActions.updateMembers(
              isReport
                ? members.map((member: { permission?: string }) => ({
                    ...member,
                    permission: 'view',
                  }))
                : members
            )
          )

          dispatch(
            notificationActionCreators.setNotification({
              type: NotificationType.INVITATION_SENT,
            })
          )

          setShowInput(false)
          setIsLoading(false)
          setValue('')
        } catch (e) {
          // TODO: handle error
          console.error(e)

          dispatch(
            notificationActionCreators.setNotification({
              type: NotificationType.SOMETHING_WENT_WRONG_ERROR,
            })
          )

          setIsLoading(false)
        }
      } else {
        !inputError && setInputError(true)
      }
    }
  }

  const color =
    value.length > 0 ? 'var(--color-code-bw-51)' : 'var(--color-code-bw-40)'

  return (
    <div className={css.invite}>
      {!showInput && (
        <div
          className={css.invitePlaceholder}
          onClick={() => setShowInput(true)}
        >
          Invite by email
        </div>
      )}

      {showInput && (
        <Recaptcha>
          <form onSubmit={onInvite}>
            <ClickAwayListener
              className={css.inviteInput}
              onClickAway={() => {
                if (inputRef.current && inputRef.current.value.length === 0) {
                  setShowInput(false)
                  setInputError(false)
                }
              }}
            >
              <input
                type="text"
                data-testid="invite_member_email"
                ref={inputRef}
                onChange={handleInputChange}
                placeholder="Invite by email"
                value={value}
              />

              <button data-testid="invite_member" style={{ color }}>
                {isLoading ? (
                  <Loading color="gray" type="small" variant="tree" />
                ) : (
                  <IconRight />
                )}
              </button>
            </ClickAwayListener>
          </form>
        </Recaptcha>
      )}

      {/* @ts-ignore*/}
      {inputError && <Error>Please check email format</Error>}
    </div>
  )
}

export default Invite
